// BranchPicker — branch/tag dropdown for the history page.
// Reuses useRepoRefs from the diff package to call /api/ws/:slug/r/:repo/refs.

import { useRepoRefs } from "../diff/useRepoRefs.js";
import { useTranslation } from "../i18n";

export interface BranchPickerProps {
  readonly slug: string;
  readonly repo: string;
  readonly value: string;
  readonly onChange: (ref: string) => void;
}

export function BranchPicker({ slug, repo, value, onChange }: BranchPickerProps) {
  const { t } = useTranslation("history");
  const { data, isPending, isError } = useRepoRefs({ slug, repo });

  const branches = data?.branches ?? [];
  const tags = data?.tags ?? [];

  return (
    <select
      className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-sm text-slate-100 transition-colors focus:border-arch-500 focus:outline-none focus:ring-2 focus:ring-arch-500/25 disabled:opacity-50"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isPending || isError}
      aria-label={t("branchPicker.ariaLabel")}
    >
      {isPending && <option value="">{t("branchPicker.loading")}</option>}
      {isError && <option value="">{t("branchPicker.loadFailed")}</option>}
      {branches.length > 0 && (
        <optgroup label={t("branchPicker.branches")}>
          {branches.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </optgroup>
      )}
      {tags.length > 0 && (
        <optgroup label={t("branchPicker.tags")}>
          {tags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
